<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { type Order } from '@/api/functions/orders/orders.action'
import { useMobileNumber } from '@/composables/useMobileNumber'
import { useOrders } from '@/composables/useOrders'
import CreateOrderDrawer from './partials/CreateOrderDrawer.vue'

const router = useRouter()
const route = useRoute()

const {
  orders: rawOrders,
  pagination,
  currentPage,
  updateFilters,
  goToPage,
} = useOrders({
  page: 1,
  sort: '-created_at',
})
const orderStatusMeta: Record<string, { label: string }> = {
  pending: { label: 'در انتظار' },
  completing_payment: { label: 'تکمیل پرداخت' },
  paid: { label: 'پرداخت شده' },
  processing: { label: 'در حال پردازش' },
  active: { label: 'فعال' },
  shipped: { label: 'ارسال شده' },
  delivered: { label: 'تحویل شده' },
  cancelled: { label: 'لغو شده' },
}

const orderStatusOptions = Object.entries(orderStatusMeta).map(([value, item]) => ({
  label: item.label,
  value,
}))

const paymentStatusMeta: Record<string, { label: string; bgColor: string; textColor: string }> = {
  paid: { label: 'پرداخت شده', bgColor: '#E6FFF0', textColor: '#2ED06E' },
  pending: { label: 'در انتظار', bgColor: '#FFF8E7', textColor: '#FFBA0E' },
  awaiting_payment: { label: 'در انتظار پرداخت', bgColor: '#FFF8E7', textColor: '#FFBA0E' },
  unpaid: { label: 'پرداخت نشده', bgColor: '#FFEDED', textColor: '#E5484D' },
  refunded: { label: 'مسترد شده', bgColor: '#EDF0F5', textColor: '#64748B' },
  failed: { label: 'ناموفق', bgColor: '#FFEDED', textColor: '#E5484D' },
}

const paymentStatusBadgeMap = Object.fromEntries(
  Object.values(paymentStatusMeta).map((m) => [m.label, { bgColor: m.bgColor, textColor: m.textColor }])
)

const filterFields = computed(() => [
  {
    type: 'input',
    key: 'search',
    label: 'شماره سفارش یا کاربر',
    title: 'جستجو',
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت سفارش',
    title: 'وضعیت سفارش',
    options: orderStatusOptions,
  },
])

const columns = [
  {
    key: 'order_number',
    label: 'سفارش',
    class: 'text-sm font-fa',
    subKey: 'createdAtLabel',
    width: '110px',
  },
  {
    key: 'orderTypeLabel',
    label: 'نوع سفارش',
    class: 'text-sm',
    width: '100px',
    badgeMap: {
      عادی: { bgColor: '#E8F5FF', textColor: '#0E59FA' },
      سفارشی: { bgColor: '#F2E3F4', textColor: '#CB30E0' },
    },
  },
  {
    key: 'userName',
    label: 'کاربر',
    class: 'text-sm',
    subKey: 'userMobile',
    subKeyCopyable: true,
    width: '140px',
    linkKey: 'userLink',
  },
  {
    key: 'itemName',
    label: 'آیتم',
    class: 'text-sm',
    subKey: 'itemMeta',
    truncate: true,
    width: '180px',
  },
  {
    key: 'planName',
    label: 'پلن',
    class: 'text-sm',
    subKey: 'planMeta',
    truncate: true,
    width: '130px',
  },
  {
    key: 'total_amount',
    label: 'مبلغ کل (تومان)',
    class: 'font-fa text-sm',
    type: 'number',
    width: '115px',
  },
  {
    key: 'tax_amount',
    label: 'مالیات (تومان)',
    class: 'font-fa text-sm',
    type: 'number',
    width: '100px',
  },
  {
    key: 'final_amount',
    label: 'مبلغ نهایی (تومان)',
    class: 'font-fa text-sm',
    type: 'number',
    width: '115px',
  },
  {
    key: 'paymentStatusLabel',
    label: 'وضعیت پرداخت',
    class: 'text-sm',
    width: '115px',
    badgeMap: paymentStatusBadgeMap,
  },
  {
    key: 'statusLabel',
    label: 'وضعیت',
    class: 'text-sm',
    width: '110px',
  },
  {
    key: 'assignedAdminName',
    label: 'ادمین',
    class: 'text-sm',
    width: '120px',
  },
]

const orders = computed(() => {
  return rawOrders.value.map((item: Order) => {
    const firstItem = item.items?.[0]
    const servicePlan = firstItem?.service_plan
    const service = servicePlan?.service

    // const dateLabel = new Intl.DateTimeFormat('fa-IR', {
    //   year: 'numeric',
    //   month: '2-digit',
    //   day: '2-digit',
    // }).format(new Date(item.created_at))

    // const timeLabel = new Intl.DateTimeFormat('fa-IR', {
    //   hour: '2-digit',
    //   minute: '2-digit',
    // }).format(new Date(item.created_at))

    const createdAtLabel = stringToFaDateTime(item.created_at)

    const categoryName = service?.categories?.map((c) => c.name).join('، ') || service?.category?.name
    const providerName = service?.provider?.name

    const durationDaysLabel = servicePlan?.duration_days != null ? `${servicePlan.duration_days} روز` : '—'

    const { whatsappLink, copyMobileNumber } = useMobileNumber(ref(item.user?.mobile), ref('98'))

    return {
      ...item,

      userName: item.user?.full_name ?? '—',
      userMobile: item.user?.mobile ?? undefined,
      userLink: item.user?.id ? `/users/${item.user.id}` : undefined,
      userMobileCopy: copyMobileNumber,
      userWhatsappLink: whatsappLink.value,

      itemName: firstItem?.name ?? '—',
      itemMeta: [categoryName, providerName].filter(Boolean).join(' · ') || undefined,

      planName: servicePlan?.name ?? '—',
      planMeta: firstItem ? `${firstItem.quantity} عدد · ${durationDaysLabel}` : undefined,

      statusLabel: orderStatusMeta[item.status]?.label ?? item.status,

      paymentStatusLabel: paymentStatusMeta[item.payment_status]?.label ?? item.payment_status,

      assignedAdminName: item.assigned_admin?.name ?? '—',

      orderTypeLabel: item.custom_service_request != null ? 'سفارشی' : 'عادی',

      createdAtLabel,
    }
  })
})

const totalItems = computed(() => pagination.value?.total ?? 0)

const itemsPerPage = computed(() => pagination.value?.perPage ?? 15)

const onApplyFilters = (values: Record<string, any>) => {
  updateFilters({
    'filter[search]': values.search ?? '',
    'filter[status]': values.status ?? '',
    sort: '-created_at',
  })

  router.push({
    path: route.path,
    query: Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    ),
  })
}
const onResetFilters = () => {
  updateFilters({
    sort: '-created_at',
  })

  goToPage(1)

  router.push({
    path: route.path,
    query: {},
  })
}

const actions = [
  {
    label: 'مشاهده',
    handlerName: 'viewOrder',
  },
  {
    label: 'واتساپ',
    handlerName: 'openWhatsapp',
  },
]

const handlePageChange = (page: number) => {
  goToPage(page)

  router.push({
    path: route.path,
    query: {
      ...route.query,
      page: String(page),
    },
  })
}

const onViewOrder = (order: Order) => {
  router.push(`/orders/${order.id}`)
}

const onOpenWhatsapp = (order: Record<string, unknown>) => {
  const link = order.userWhatsappLink as string | undefined
  if (!link) return

  window.open(link, '_blank', 'noopener,noreferrer')
}

onMounted(() => {
  if (Object.keys(route.query).length === 0) {
    return
  }

  updateFilters({
    'filter[search]': typeof route.query.search === 'string' ? route.query.search : '',

    'filter[status]': typeof route.query.status === 'string' ? route.query.status : '',

    sort: '-created_at',

    page: route.query.page ? Number(route.query.page) : 1,
  })
})
</script>

<template>
  <div class="w-full mb-12">
    <div class="flex items-center justify-between">
      <h2 class="text-[22px] text-[#1b325a]">
        لیست سفارش‌ها
      </h2>
      <CreateOrderDrawer />
    </div>


    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-20 lg:mt-0" />

    <div class="flex flex-col gap-2 mt-3 min-w-0">
      <Table :columns="columns" :rows="orders" :actions="actions" @primaryAction="onViewOrder"
        @secondaryAction="onOpenWhatsapp" />

      <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
        :totalItems="totalItems" @update:currentPage="handlePageChange" :isAdmin="true" />
    </div>
  </div>
</template>
